import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Chip,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  TablePagination,
  Tooltip
} from '@mui/material';
import { CheckCircle, XCircle, ChevronDown, ChevronUp, Edit, Save, X, Link2, Unlink } from 'lucide-react';
import { FieldMapping, ImportedRecord, ImportType, RelationshipMapping, RELATIONSHIP_CONFIGS } from '../BulkImport';

interface PreviewStepProps {
  importedRecords: ImportedRecord[];
  fieldMappings: FieldMapping[];
  relationshipMappings: RelationshipMapping[];
  importType: ImportType;
  onRecordsChange: (records: ImportedRecord[]) => void;
}

const FIELD_LABELS: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  studentId: 'Student ID',
  email: 'Email',
  phone: 'Phone',
  gradeLevel: 'Grade Level',
  dateOfBirth: 'Date of Birth',
  address: 'Address',
  city: 'City',
  state: 'State',
  zipCode: 'Zip Code',
  organization: 'Organization',
  role: 'Role',
  name: 'Name',
  description: 'Description',
  amount: 'Amount',
  category: 'Category',
  dueDate: 'Due Date',
  isRequired: 'Is Required',
  allowMultiple: 'Allow Multiple',
  maxQuantity: 'Max Quantity',
  instructor: 'Instructor',
  capacity: 'Capacity',
  schedule: 'Schedule',
  location: 'Location',
  startDate: 'Start Date',
  endDate: 'End Date'
};

export const PreviewStep = ({ importedRecords, fieldMappings, relationshipMappings, importType, onRecordsChange }: PreviewStepProps) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editData, setEditData] = useState<Record<string, string>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const relationshipConfigs = RELATIONSHIP_CONFIGS[importType] || [];
  const activeRelationships = relationshipMappings.filter(m => m.csvColumn !== null);

  const validRecords = importedRecords.filter(r => r.isValid);
  const invalidRecords = importedRecords.filter(r => !r.isValid);

  const mappedFields = fieldMappings.filter(m => m.csvColumn !== null).map(m => m.systemField);

  const getRelationshipStats = () => {
    let matched = 0;
    let unmatched = 0;

    importedRecords.forEach(record => {
      if (record.relationshipMatches) {
        Object.values(record.relationshipMatches).forEach(match => {
          if (match.csvValue && match.csvValue.trim() !== '') {
            if (match.matchedId) {
              matched++;
            } else {
              unmatched++;
            }
          }
        });
      }
    });

    return { matched, unmatched };
  };

  const relationshipStats = getRelationshipStats();

  const handleToggleRow = (id: string) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const handleEditRow = (record: ImportedRecord) => {
    setEditingRow(record.id);
    setEditData(record.data);
  };

  const handleSaveEdit = (id: string) => {
    const updatedRecords = importedRecords.map(record => {
      if (record.id === id) {
        const validation = validateRecord(editData);
        return {
          ...record,
          data: editData,
          isValid: validation.isValid,
          errors: validation.errors
        };
      }
      return record;
    });

    onRecordsChange(updatedRecords);
    setEditingRow(null);
    setEditData({});
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditData({});
  };

  const handleEditChange = (field: string, value: string) => {
    setEditData({ ...editData, [field]: value });
  };

  const validateRecord = (data: Record<string, string>): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (importType === 'students') {
      if (!data.firstName) errors.push('First name is required');
      if (!data.lastName) errors.push('Last name is required');
      if (!data.studentId) errors.push('Student ID is required');
      if (!data.email) errors.push('Email is required');
      if (data.email && !isValidEmail(data.email)) errors.push('Invalid email format');
    } else if (importType === 'parents') {
      if (!data.firstName) errors.push('First name is required');
      if (!data.lastName) errors.push('Last name is required');
      if (!data.email) errors.push('Email is required');
      if (data.email && !isValidEmail(data.email)) errors.push('Invalid email format');
    } else if (importType === 'community') {
      if (!data.firstName) errors.push('First name is required');
      if (!data.lastName) errors.push('Last name is required');
      if (!data.email) errors.push('Email is required');
      if (data.email && !isValidEmail(data.email)) errors.push('Invalid email format');
    } else if (importType === 'fees') {
      if (!data.name) errors.push('Name is required');
      if (!data.amount) errors.push('Amount is required');
      if (data.amount && isNaN(parseFloat(data.amount))) errors.push('Amount must be a valid number');
      if (!data.category) errors.push('Category is required');
    } else if (importType === 'courses') {
      if (!data.name) errors.push('Name is required');
      if (!data.instructor) errors.push('Instructor is required');
      if (data.capacity && isNaN(parseInt(data.capacity))) errors.push('Capacity must be a valid number');
    }

    return { isValid: errors.length === 0, errors };
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRecords = importedRecords.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Preview Import Data
      </Typography>

      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2">
            <strong>Total Records:</strong> {importedRecords.length}
          </Typography>
          <Chip
            icon={<CheckCircle size={16} />}
            label={`Valid: ${validRecords.length}`}
            color="success"
            size="small"
          />
          {invalidRecords.length > 0 && (
            <Chip
              icon={<XCircle size={16} />}
              label={`Invalid: ${invalidRecords.length}`}
              color="error"
              size="small"
            />
          )}
        </Box>
        {activeRelationships.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2">
              <strong>Relationships:</strong>
            </Typography>
            <Chip
              icon={<Link2 size={16} />}
              label={`Matched: ${relationshipStats.matched}`}
              color="primary"
              size="small"
            />
            {relationshipStats.unmatched > 0 && (
              <Chip
                icon={<Unlink size={16} />}
                label={`Unmatched: ${relationshipStats.unmatched}`}
                color="warning"
                size="small"
              />
            )}
          </Box>
        )}
      </Paper>

      {invalidRecords.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {invalidRecords.length} record{invalidRecords.length !== 1 ? 's' : ''} contain errors and will not be imported.
          You can edit records to fix validation issues.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Status</TableCell>
              {mappedFields.map(field => (
                <TableCell key={field}>{FIELD_LABELS[field] || field}</TableCell>
              ))}
              {activeRelationships.length > 0 && (
                <TableCell>Links</TableCell>
              )}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRecords.map((record) => (
              <>
                <TableRow
                  key={record.id}
                  sx={{
                    bgcolor: !record.isValid ? 'error.light' : undefined,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <TableCell padding="checkbox">
                    <IconButton size="small" onClick={() => handleToggleRow(record.id)}>
                      {expandedRow === record.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    {record.isValid ? (
                      <Chip icon={<CheckCircle size={16} />} label="Valid" color="success" size="small" />
                    ) : (
                      <Chip icon={<XCircle size={16} />} label="Invalid" color="error" size="small" />
                    )}
                  </TableCell>
                  {editingRow === record.id ? (
                    <>
                      {mappedFields.map(field => (
                        <TableCell key={field}>
                          <TextField
                            size="small"
                            fullWidth
                            value={editData[field] || ''}
                            onChange={(e) => handleEditChange(field, e.target.value)}
                          />
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => handleSaveEdit(record.id)}>
                          <Save size={16} />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelEdit}>
                          <X size={16} />
                        </IconButton>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      {mappedFields.map(field => (
                        <TableCell key={field}>{record.data[field] || '-'}</TableCell>
                      ))}
                      {activeRelationships.length > 0 && (
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {activeRelationships.map(relMapping => {
                              const match = record.relationshipMatches?.[relMapping.relationshipId];
                              const config = relationshipConfigs.find(c => c.id === relMapping.relationshipId);
                              if (!match || !match.csvValue || match.csvValue.trim() === '') return null;

                              return (
                                <Tooltip
                                  key={relMapping.relationshipId}
                                  title={match.matchedId
                                    ? `${config?.label}: ${match.matchedLabel}`
                                    : `${config?.label}: "${match.csvValue}" not found`
                                  }
                                >
                                  <Chip
                                    icon={match.matchedId ? <Link2 size={12} /> : <Unlink size={12} />}
                                    label={config?.label?.split(' ')[0] || 'Link'}
                                    size="small"
                                    color={match.matchedId ? 'primary' : 'warning'}
                                    variant={match.matchedId ? 'filled' : 'outlined'}
                                    sx={{ fontSize: '0.65rem', height: 22 }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Box>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEditRow(record)}>
                          <Edit size={16} />
                        </IconButton>
                      </TableCell>
                    </>
                  )}
                </TableRow>
                {expandedRow === record.id && (record.errors.length > 0 || (record.relationshipMatches && Object.keys(record.relationshipMatches).length > 0)) && (
                  <TableRow>
                    <TableCell colSpan={mappedFields.length + (activeRelationships.length > 0 ? 4 : 3)} sx={{ py: 0 }}>
                      <Collapse in={expandedRow === record.id}>
                        {record.errors.length > 0 && (
                          <Box sx={{ p: 2, bgcolor: 'error.light' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Validation Errors:
                            </Typography>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                              {record.errors.map((error, idx) => (
                                <li key={idx}>
                                  <Typography variant="body2">{error}</Typography>
                                </li>
                              ))}
                            </ul>
                          </Box>
                        )}
                        {record.relationshipMatches && Object.keys(record.relationshipMatches).length > 0 && (
                          <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Link2 size={16} /> Relationship Details:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {Object.entries(record.relationshipMatches).map(([relId, match]) => {
                                if (!match.csvValue || match.csvValue.trim() === '') return null;
                                const config = relationshipConfigs.find(c => c.id === relId);
                                return (
                                  <Box key={relId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {match.matchedId ? (
                                      <Chip icon={<Link2 size={14} />} label="Matched" size="small" color="success" sx={{ minWidth: 90 }} />
                                    ) : (
                                      <Chip icon={<Unlink size={14} />} label="Not Found" size="small" color="warning" sx={{ minWidth: 90 }} />
                                    )}
                                    <Typography variant="body2">
                                      <strong>{config?.label}:</strong>{' '}
                                      "{match.csvValue}"{' '}
                                      {match.matchedId ? (
                                        <span style={{ color: '#16a34a' }}>
                                          → {match.matchedLabel}
                                        </span>
                                      ) : (
                                        <span style={{ color: '#d97706' }}>
                                          (no matching record found)
                                        </span>
                                      )}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={importedRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {validRecords.length === 0 && (
        <Alert severity="error" sx={{ mt: 3 }}>
          No valid records to import. Please fix the validation errors or upload a different file.
        </Alert>
      )}
    </Box>
  );
};
